import { formatDate } from "../../../utils/date";
import { formatNumber } from "../../../utils";
import { DeductionEmployeeSummary } from "../../../types/deduction.type";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Divider from "@mui/material/Divider";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import IconButton from "@mui/material/IconButton";
import AirportShuttleOutlinedIcon from "@mui/icons-material/AirportShuttleOutlined";
import RoleChip from "../RoleChip";

interface StaffSummaryProps {
  summary: DeductionEmployeeSummary;
}

const headerCellSx = {
  borderRight: (theme: any) => `1px solid ${theme.palette.draft.main}`,
};

export default function StaffSummary({ summary }: StaffSummaryProps) {
  const monthLabel = formatDate(`${summary.year_month}-01`, "MMMM-YYYY");

  return (
    <Paper sx={{ p: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={500}>
            {summary.fullname}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {monthLabel}
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography variant="body2" color="text.secondary">
            ยอดหักรวมทั้งเดือน
          </Typography>
          <Typography color="error" variant="h6" fontWeight={600}>
            -{formatNumber(summary.total_amount)} ฿
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {summary.day_count} วันที่ถูกหัก
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              <TableCell sx={headerCellSx}>วันที่</TableCell>
              <TableCell sx={headerCellSx}>สายส่ง</TableCell>
              <TableCell sx={headerCellSx}>ตำแหน่ง</TableCell>
              <TableCell sx={headerCellSx}>ยอดหัก</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summary.rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูล
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {summary.rows.map((row, index) => (
              <TableRow
                key={`${row.date}-${row.route_uuid}`}
                sx={{
                  bgcolor: index % 2 === 1 ? "#f6f6f9" : "#fff",
                  "& td, & th": { padding: "8px" },
                }}
              >
                <TableCell>{formatDate(row.date)}</TableCell>
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconButton
                      sx={{
                        bgcolor: (theme) => theme.palette.primary.main + "1A",
                        cursor: "default",
                        "&:hover, &:focus": {
                          bgcolor: (theme) => theme.palette.primary.main + "1A",
                        },
                      }}
                    >
                      <AirportShuttleOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: (theme) => theme.palette.primary.main,
                        }}
                      />
                    </IconButton>
                    <span>{row.route_code}</span>
                  </Stack>
                </TableCell>
                <TableCell>
                  <RoleChip role={row.role} />
                </TableCell>
                <TableCell>
                  <Typography color="error">
                    -{formatNumber(row.share_amount)} ฿
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
